import { loadContentData } from "@src/packages/content/loader";
import { getBlocksByKind } from "@src/packages/content";
import type {
  DataPointBody,
  DataPointSampleType,
} from "@src/packages/content/types";
import { normalizeBiomarkerName } from "./biomarker-identity";
import { getMarkerUnits } from "./markerUnits";

loadContentData();

/** A test a value can be recorded for: a content data point and its units. */
export type LabTest = {
  id: string;
  title: string;
  aliases: string[];
  /** The units a lab reports it in, the conventional one first. */
  units: readonly string[];
  sampleTypes: DataPointSampleType[];
};

/** The markers a patient is most likely to be adding, first. */
const COMMON_FIRST = [
  "dp-amh-level",
  "dp-fsh-level",
  "dp-lh-level",
  "dp-estradiol-level",
  "dp-progesterone-level",
  "dp-beta-hcg-level",
  "dp-tsh-level",
  "dp-prolactin-level",
];

function rank(test: LabTest): number {
  const index = COMMON_FIRST.indexOf(test.id);
  return index === -1 ? COMMON_FIRST.length : index;
}

/**
 * The test catalogue, common markers first. With sample types, only the tests
 * measured from one of them, so a blood report offers blood tests.
 */
export function getLabTests(
  sampleTypes?: readonly DataPointSampleType[] | null,
): LabTest[] {
  const tests = getBlocksByKind("data-point").map((block) => {
    const body = block.body as DataPointBody;
    const knownUnits = getMarkerUnits(block.title);
    return {
      id: block.id,
      title: block.title,
      aliases: body.aliases ?? [],
      units: knownUnits.length > 0 ? knownUnits : body.unit ? [body.unit] : [],
      sampleTypes: body.sampleTypes ?? [],
    };
  });
  const offered =
    sampleTypes && sampleTypes.length > 0
      ? tests.filter((test) =>
          test.sampleTypes.some((type) => sampleTypes.includes(type)),
        )
      : tests;
  return [...offered].sort(
    (left, right) =>
      rank(left) - rank(right) || left.title.localeCompare(right.title),
  );
}

/** The tests whose name or an alias contains the query. */
export function searchLabTests(
  tests: readonly LabTest[],
  query: string,
): LabTest[] {
  const needle = normalizeBiomarkerName(query);
  if (!needle) return [...tests];
  return tests.filter((test) =>
    [test.title, ...test.aliases].some((name) =>
      normalizeBiomarkerName(name).includes(needle),
    ),
  );
}

/** The catalogue test a name refers to exactly, if any. */
export function findLabTest(
  tests: readonly LabTest[],
  name: string,
): LabTest | undefined {
  const query = normalizeBiomarkerName(name);
  if (!query) return undefined;
  return tests.find((test) =>
    [test.title, ...test.aliases].some(
      (candidate) => normalizeBiomarkerName(candidate) === query,
    ),
  );
}
